import { useCallback, useEffect, useId, useRef, useState } from 'react';
import FenceConnectionLink from '../FenceConnectionLink';
import { Link } from 'react-router-dom';
import type { ApiClient } from '../../api/client';

type Choice = { id: string; name: string; model: string; funding: 'application_api' | 'shared_subscription'; expires_at?: string | null };
type Catalog = { items: Choice[]; selected_id: string | null; available: boolean; selected: Choice | null };
const modelOrder = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/** This control belongs to the trusted shell. Changing it never remounts chat. */
export default function ModelPicker({ apiClient, visible, detailed = true }: { apiClient: ApiClient; visible: boolean; detailed?: boolean }) {
  const modelId = useId();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const refresh = useCallback(async () => {
    const current = ++generation.current;
    const value = await apiClient.get<Catalog>('/api/assistant/gateway/models');
    if (generation.current === current) setCatalog(value);
  }, [apiClient]);
  useEffect(() => {
    if (!visible) return;
    let disposed = false;
    const poll = async () => {
      const current = ++generation.current;
      try { const value = await apiClient.get<Catalog>('/api/assistant/gateway/models'); if (!disposed && generation.current === current) { setCatalog(value); setError(null); } }
      catch { if (!disposed && generation.current === current) setError('Models could not be checked. Your conversation is saved.'); }
    };
    void poll();
    window.addEventListener('focus', poll);
    const timer = setInterval(() => { void poll(); }, 30000);
    return () => { disposed = true; clearInterval(timer); window.removeEventListener('focus', poll); };
  }, [apiClient, visible]);

  const select = async (id: string) => {
    if (!id || busy) return;
    setBusy(true); setError(null);
    ++generation.current;
    try {
      const result = await apiClient.post<{ selected: Choice }>('/api/assistant/gateway/models', { source_id: id });
      ++generation.current;
      setCatalog(current => current ? { ...current, selected_id: result.selected.id, selected: result.selected, available: true } : current);
      await refresh();
    } catch { setError('The model change could not be confirmed. Check the selection before sending.'); }
    finally { setBusy(false); }
  };

  return <div className="assistant-model-picker">
    <div className="assistant-model-row"><label htmlFor={modelId}>Model</label>
      <select id={modelId} value={catalog?.selected_id ?? ''} disabled={busy || !catalog}
        onChange={event => { void select(event.target.value); }}>
        {!catalog?.selected_id && <option value="">{catalog ? 'Choose an available model' : 'Loading models…'}</option>}
        {catalog?.selected_id && !catalog.items.some(c => c.id === catalog.selected_id)
          && <option value={catalog.selected_id}>{catalog.selected?.name ?? 'Selected model'} · unavailable</option>}
        {(['shared_subscription', 'application_api'] as const).map(funding => {
          const items = (catalog?.items.filter(c => c.funding === funding) ?? [])
            .sort((a, b) => modelOrder.compare(a.name, b.name) || modelOrder.compare(a.model, b.model) || a.id.localeCompare(b.id));
          return items.length > 0 ? <optgroup key={funding} label={funding === 'application_api' ? 'API · Fence limits' : 'Shared subscription'}>
            {items.map(choice => <option key={choice.id} value={choice.id}>{choice.name}</option>)}
          </optgroup> : null;
        })}
      </select>
      <button type="button" className="ghost" aria-label="Refresh available models" disabled={busy} onClick={() => { void refresh().then(() => setError(null)).catch(() => setError('Models could not be checked.')); }}>↻</button>
    </div>
    <p className="assistant-model-hint" role="status">{busy ? 'Saving your model…' : !catalog ? 'Checking the model for your next message…' : !catalog.available
      ? 'This model is unavailable. Choose another to continue.'
      : `${catalog?.selected?.funding === 'shared_subscription' ? 'Shared subscription · Fence limits' : 'API · Fence limits'} · applies to your next message`}</p>
    {detailed && <FenceConnectionLink apiClient={apiClient} />}
    {detailed && catalog && !catalog.items.some(choice => choice.funding === 'shared_subscription')
      && <p className="assistant-model-hint">Subscription missing? In Fence, connect your account, then use “Allow subscription use” to authorize a pool and model. Refresh this list when finished.</p>}
    {detailed && <Link className="assistant-sources-link" to="/settings/models">Manage models and subscriptions</Link>}
    {error && <p className="assistant-model-error" role="alert">{error}</p>}
  </div>;
}
